// Renders deliverables whose `milestoneId` FK points at a milestone — the
// single canonical link between a milestone and its deliverables. Read-only
// surface; the editable mirror is MilestoneDeliverablesPicker (inline-edit).
//
// Lives in its own leaf module so MilestoneDetailExpand (view mode) and
// MilestoneTab can consume it without taking on shared-mock coupling.

import LoadingSpinner from "../../../common/LoadingSpinner";
import TrackingStatusBadge from "../shared/TrackingStatusBadge";
import { deriveDeliverableStatus } from "../deliverable/deriveDeliverableStatus";
import { deriveOccurrenceTitle } from "../deliverable/deriveOccurrenceTitle";
import { formatDayMonthYear } from "../../../../utils/formatShortDate";
import type { Deliverable } from "../../../../types";

export interface LinkedDeliverablesSectionProps {
  query: {
    data: Deliverable[] | undefined;
    /** Either React Query name works — `isPending` is the modern name,
     *  `isLoading` is the historical alias. Both map to the same "first
     *  fetch in progress" condition. Callers may set either. */
    isPending?: boolean;
    isLoading?: boolean;
    error: unknown;
  };
  onNavigate?: (deliverableId: string) => void;
}

export default function LinkedDeliverablesSection({
  query,
  onNavigate,
}: LinkedDeliverablesSectionProps) {
  const loading = query.isPending ?? query.isLoading ?? false;
  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/50">
      <p className="text-caption font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
        Deliverables in this milestone
      </p>

      {loading && (
        <div data-testid="linked-deliverables-loading" className="py-1">
          <LoadingSpinner size="sm" />
        </div>
      )}

      {!loading && !!query.error && (
        <div
          data-testid="linked-deliverables-error"
          className="text-caption text-red-600 dark:text-red-400"
        >
          Could not load linked deliverables.
        </div>
      )}

      {!loading && !query.error && query.data && query.data.length === 0 && (
        <p className="text-caption text-slate-400 dark:text-slate-500">
          No deliverables are linked to this milestone yet. Assign deliverables via
          the deliverable detail&apos;s milestone field.
        </p>
      )}

      {!loading && !query.error && query.data && query.data.length > 0 && (
        <ul className="space-y-1">
          {query.data.map((d) => {
            const title = deriveOccurrenceTitle(d);
            const derivedStatus = deriveDeliverableStatus(
              d.status,
              d.dueDate,
              new Date(),
              d.submittedAt,
            );
            return (
              <li key={d.id}>
                {onNavigate ? (
                  <button
                    type="button"
                    onClick={() => onNavigate(d.id)}
                    className="w-full flex items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left hover:bg-slate-100 dark:hover:bg-slate-700/50 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400"
                  >
                    <span className="text-body-sm text-slate-900 dark:text-slate-100 truncate">
                      {title}
                    </span>
                    <span className="flex items-center gap-2 shrink-0">
                      <TrackingStatusBadge kind="deliverable" status={derivedStatus} />
                      {d.dueDate && (
                        <span className="text-caption text-slate-500 dark:text-slate-400">
                          {formatDayMonthYear(d.dueDate)}
                        </span>
                      )}
                    </span>
                  </button>
                ) : (
                  <div className="flex items-center justify-between gap-3 rounded-md px-2 py-1.5">
                    <span className="text-body-sm text-slate-900 dark:text-slate-100 truncate">
                      {title}
                    </span>
                    <span className="flex items-center gap-2 shrink-0">
                      <TrackingStatusBadge kind="deliverable" status={derivedStatus} />
                      {d.dueDate && (
                        <span className="text-caption text-slate-500 dark:text-slate-400">
                          {formatDayMonthYear(d.dueDate)}
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
